import React, { useState } from 'react';
import { Alert, Button, ConfirmActionModal, Modal } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { isApiError } from '@/services/api';
import { useFeeReferenceDataQuery } from '../hooks/useFeeReferenceDataQuery';
import { useProductFeeMutations } from '../hooks/useProductFeeMutations';
import { usePayerPairOverrideMutations } from '../hooks/usePayerPairOverrideMutations';
import { useProductFeesQuery } from '../hooks/useProductFeesQuery';
import type { PayerPairOverridePayload, ProductFeePayload, ProductFeeRecord } from '../services/productFees';
import { defaultPairOfTransactionType } from '../utils/routablePayerPairs';
import { ProductFeeDataTable } from './ProductFeeDataTable';
import { PayerPairOverrideForm, type ReferenceStatus } from './PayerPairOverrideForm';
import { ProductFeeForm } from './ProductFeeForm';

interface Props {
    productId: string;
}

type ModalState =
    | { type: 'create' }
    | { type: 'edit'; row: ProductFeeRecord }
    | { type: 'override'; row: ProductFeeRecord }
    | null;

export const ProductFeePanel: React.FC<Props> = ({ productId }) => {
    const { data = [], isLoading } = useProductFeesQuery(productId);
    const { data: refData, isError: refFailed } = useFeeReferenceDataQuery();
    const { createMutation, updateMutation, deleteMutation } = useProductFeeMutations(productId);
    const { setMutation, clearMutation } = usePayerPairOverrideMutations(productId);
    const [clearTarget, setClearTarget] = useState<ProductFeeRecord | null>(null);
    const [clearError, setClearError] = useState<string | null>(null);
    const [modalState, setModalState] = useState<ModalState>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

    const handleCreate = (payload: ProductFeePayload) => {
        createMutation.mutate(payload, {
            onSuccess: () => {
                setModalState(null);
                setFormError(null);
            },
            onError: (err) => {
                setFormError(isApiError(err) ? err.message : 'Failed to add fee.');
            },
        });
    };

    const handleUpdate = (row: ProductFeeRecord) => (payload: ProductFeePayload) => {
        updateMutation.mutate(
            { id: row.id, payload },
            {
                onSuccess: () => {
                    setModalState(null);
                    setFormError(null);
                },
                onError: (err) => {
                    setFormError(isApiError(err) ? err.message : 'Failed to update fee.');
                },
            },
        );
    };

    const handleOverride = (row: ProductFeeRecord) => (payload: PayerPairOverridePayload) => {
        setMutation.mutate(
            { id: row.id, payload },
            {
                onSuccess: () => {
                    setModalState(null);
                    setFormError(null);
                },
                onError: (err) => {
                    setFormError(isApiError(err) ? err.message : 'Failed to override the payer pair.');
                },
            },
        );
    };

    const referenceStatus: ReferenceStatus = refData ? 'ready' : refFailed ? 'error' : 'loading';

    const openClear = (row: ProductFeeRecord) => {
        setClearError(null);
        setClearTarget(row);
    };

    const closeClear = () => {
        setClearError(null);
        setClearTarget(null);
    };

    const handleConfirmClear = () => {
        if (!clearTarget) return;
        clearMutation.mutate(clearTarget.id, {
            onSuccess: closeClear,
            onError: (err) => {
                setClearError(isApiError(err) ? err.message : 'Failed to remove the payer pair override. Try again.');
            },
        });
    };

    const handleDelete = (id: string) => {
        setDeleteTargetId(id);
    };

    const handleConfirmDelete = () => {
        if (deleteTargetId) deleteMutation.mutate(deleteTargetId);
        setDeleteTargetId(null);
    };

    const handleClose = () => {
        setModalState(null);
        setFormError(null);
    };

    const handleCloseOverride = () => {
        if (!setMutation.isPending) handleClose();
    };

    if (isLoading) return <p className="text-sm text-mp-muted">Loading fees...</p>;

    return (
        <div className="space-y-4">
            {data.length === 0 ? (
                <p className="text-sm text-mp-muted">No fees configured for this product.</p>
            ) : (
                <ProductFeeDataTable
                    data={data}
                    onEdit={(row) => {
                        setFormError(null);
                        setModalState({ type: 'edit', row });
                    }}
                    onDelete={handleDelete}
                    onOverridePayerPair={(row) => {
                        setFormError(null);
                        setModalState({ type: 'override', row });
                    }}
                    onClearPayerPairOverride={openClear}
                    isLoading={deleteMutation.isPending}
                    activeQuoteTypes={refData?.quote_types ?? []}
                />
            )}

            <PermissionGuard permission={{ resource: 'smr', action: 'product_fee.add' }}>
                <div className="border-t border-mp-border pt-4">
                    <Button
                        size="sm"
                        variant="primary"
                        type="button"
                        onClick={() => {
                            setFormError(null);
                            setModalState({ type: 'create' });
                        }}
                    >
                        Add Fee
                    </Button>
                </div>
            </PermissionGuard>

            <Modal
                isOpen={modalState?.type === 'override'}
                onClose={handleCloseOverride}
                ariaLabel="Override payer pair"
                className="max-w-xl p-6"
            >
                <h2 className="text-lg font-semibold mb-4">Override Payer Pair</h2>
                {modalState?.type === 'override' && (
                    <PayerPairOverrideForm
                        targets={refData?.fee_targets ?? []}
                        referenceStatus={referenceStatus}
                        defaultPair={defaultPairOfTransactionType(modalState.row.fee.transaction_type?.id, refData?.transaction_types ?? [])}
                        initial={modalState.row.payer_pair_overridden && modalState.row.paid_by && modalState.row.paid_to
                            ? { paid_by_id: modalState.row.paid_by.id, paid_to_id: modalState.row.paid_to.id }
                            : undefined}
                        onSubmit={handleOverride(modalState.row)}
                        isLoading={setMutation.isPending}
                        error={formError}
                        onCancel={handleCloseOverride}
                    />
                )}
            </Modal>

            <Modal
                isOpen={modalState?.type === 'create' || modalState?.type === 'edit'}
                onClose={handleClose}
                ariaLabel={modalState?.type === 'edit' ? 'Edit product fee' : 'Add product fee'}
                className="max-w-2xl p-6"
            >
                <h2 className="text-lg font-semibold mb-4">
                    {modalState?.type === 'edit' ? 'Edit Fee' : 'Add Fee'}
                </h2>
                {(modalState?.type === 'create' || modalState?.type === 'edit') && (
                    <ProductFeeForm
                        mode={modalState.type === 'edit' ? 'edit' : 'create'}
                        row={modalState.type === 'edit' ? modalState.row : undefined}
                        onSubmit={modalState.type === 'edit' ? handleUpdate(modalState.row) : handleCreate}
                        isLoading={createMutation.isPending || updateMutation.isPending}
                        error={formError}
                        onCancel={handleClose}
                    />
                )}
            </Modal>

            <ConfirmActionModal
                isOpen={clearTarget !== null}
                title="Use default pair"
                message={
                    <>
                        {clearError && <Alert variant="error" className="mb-3">{clearError}</Alert>}
                        Remove the payer pair override? Future quotes and agreements will use the transaction type's default pair. Existing agreements keep the pair they were sold with.
                    </>
                }
                confirmLabel="Use default pair"
                confirmVariant="danger"
                isPending={clearMutation.isPending}
                onConfirm={handleConfirmClear}
                onClose={closeClear}
            />

            <ConfirmActionModal
                isOpen={deleteTargetId !== null}
                title="Delete product fee"
                message="Delete this product fee?"
                confirmLabel="Delete"
                confirmVariant="danger"
                isPending={deleteMutation.isPending}
                onConfirm={handleConfirmDelete}
                onClose={() => setDeleteTargetId(null)}
            />
        </div>
    );
};
